import { ViewModel, type ContactSubmit } from "@lumora/core";
import { type PackageId } from "../models/PackageCatalog.js";
import { QuoteRequest } from "../models/QuoteRequest.js";

export interface QuoteState {
  name: string;
  email: string;
  category: string;
  budget: string;
  packageId: PackageId | "";
  skuCount: number;
  details: string;
  estimateUsd: number | null;
  errors: string[];
  submitting: boolean;
  submitted: boolean;
}

/** Binds the quote form View to a QuoteRequest and the shared contact submit path. */
export class QuoteViewModel extends ViewModel<QuoteState> {
  constructor(private readonly onSubmit: ContactSubmit, initialPackage: PackageId | "" = "") {
    super({
      name: "",
      email: "",
      category: "",
      budget: "",
      packageId: initialPackage,
      skuCount: 1,
      details: "",
      estimateUsd: null,
      errors: [],
      submitting: false,
      submitted: false,
    });
    this.refreshEstimate();
  }

  setName(name: string): void { this.setState({ name }); }
  setEmail(email: string): void { this.setState({ email }); }
  setCategory(category: string): void { this.setState({ category }); }
  setBudget(budget: string): void { this.setState({ budget }); }
  setDetails(details: string): void { this.setState({ details }); }
  setPackage(packageId: PackageId | ""): void { this.setState({ packageId }); this.refreshEstimate(); }
  setSkuCount(skuCount: number): void { this.setState({ skuCount }); this.refreshEstimate(); }

  private request(): QuoteRequest {
    const s = this.state;
    return new QuoteRequest({
      name: s.name, email: s.email, category: s.category, budget: s.budget,
      packageId: s.packageId, skuCount: s.skuCount, details: s.details,
    });
  }

  private refreshEstimate(): void {
    this.setState({ estimateUsd: this.request().estimateUsd() });
  }

  async submit(): Promise<boolean> {
    const req = this.request();
    const errors = req.validate();
    if (errors.length > 0) {
      this.setState({ errors });
      return false;
    }
    const ok = await this.run(
      async () => {
        await this.onSubmit(req.toContactMessage());
        this.setState({ submitted: true });
        return true;
      },
      "Could not send your request. Please try again.",
      { flag: "submitting", listErrors: true },
    );
    return ok === true;
  }
}
